import { Options } from "../risks/iso-verklaringen";

// ──────────────────────────────────────────────
// Default range bounds
// ──────────────────────────────────────────────
export const DEFAULT_RANGE_BOUNDS = [6, 14];

// ──────────────────────────────────────────────
// Risk severity helpers (rangeBounds-based)
// ──────────────────────────────────────────────

/**
 * Returns the severity key ('low' | 'medium' | 'high') for a given score.
 */
export const getRiskSeverity = (score, bounds = DEFAULT_RANGE_BOUNDS) => {
  if (score <= bounds[0]) return 'low';
  if (score <= bounds[1]) return 'medium';
  return 'high';
};

/**
 * Returns a CSS class for a bordered score circle: score-border-green / yellow / red.
 */
export const getScoreBorderClass = (score, bounds = DEFAULT_RANGE_BOUNDS) => {
  return `score-border-${getRiskColor(score, bounds)}`;
};

/**
 * Returns a CSS class for background-filled risk score: risk-score-green / yellow / red.
 */
export const getRiskScoreClass = (score, bounds = DEFAULT_RANGE_BOUNDS) => {
  return `risk-score-${getRiskColor(score, bounds)}`;
};

/**
 * Returns a CSS class for input score coloring: input-score-color-green / yellow / red.
 */
export const getInputScoreColorClass = (score, bounds = DEFAULT_RANGE_BOUNDS) => {
  return `input-score-color-${getRiskColor(score, bounds)}`;
};

/**
 * Whether we should suggest changing the risk treatment from 'reduce' to 'accept'.
 * True when the treatment is still 'reduce' but the current score has already reached
 * (or dropped below) the target score, meaning no further reduction is needed.
 *
 * Optionally accepts explicit chance/impact overrides so callers (e.g. the score
 * update modal) can evaluate the suggestion against in-progress, unsaved values.
 */
// Admins, or the user who created the risk, may archive/restore it (same rule deleting had).
export const canArchiveRisk = (user, risk) =>
  !!user && !!risk && (user.role === 'superadmin' || user.role === 'admin' || risk.createdBy === user.user_id);

export const shouldSuggestAcceptTreatment = (risk, overrides = {}) => {
  if (!risk || risk.treatment !== 'reduce') return false;

  const actualChance = overrides.actualChance ?? risk['actual-chance'];
  const actualImpact = overrides.actualImpact ?? risk['actual-impact'];

  const currentScore = Number(actualChance) * Number(actualImpact);
  const targetScore = Number(risk['expected-chance']) * Number(risk['expected-impact']);

  if (!currentScore || !targetScore) return false;

  return currentScore <= targetScore;
};

/**
 * Returns the base color name ('green' | 'yellow' | 'red') for a given score.
 */
export const getRiskColor = (score, bounds = DEFAULT_RANGE_BOUNDS) => {
  if (score <= bounds[0]) return 'green';
  if (score <= bounds[1]) return 'yellow';
  return 'red';
};

/**
 * Returns the CSS variable value for a risk score color.
 */
export const getRiskColorVar = (score, bounds = DEFAULT_RANGE_BOUNDS) => {
  const colorMap = {
    green: 'var(--green-risk-score)',
    yellow: 'var(--yellow-risk-score)',
    red: 'var(--red-risk-score)',
  };
  return colorMap[getRiskColor(score, bounds)];
};

/**
 * Returns a CSS variable for a severity level string ('low' | 'medium' | 'high').
 */
export const getSeverityColorVar = (level) => {
  if (level === 'high') return 'var(--red-risk-score)';
  if (level === 'medium') return 'var(--yellow-risk-score)';
  return 'var(--green-risk-score)';
};

// ──────────────────────────────────────────────
// General helpers
// ──────────────────────────────────────────────

export const replaceUnderscoresAndUppercase = (str) => {
    // Replace underscores with spaces
    str = str.replace(/_/g, ' ');

    // Uppercase the first character and every character after a space
    str = str.replace(/(^|\s)(\w)/g, function(match, p1, p2) {
        return p1 + p2.toUpperCase();
    });

    return str;
};

/**
 * Normalize a category entry (string ID or {value, label} object) to its ID string.
 * Handles both the new DB format (plain string) and legacy format (object).
 */
export const getCategoryValue = (cat) => {
    return typeof cat === 'string' ? cat : cat?.value;
};

/**
 * Expand an array of category entries (string IDs or objects) to full {value, label} objects
 * suitable for the Typeahead component. Looks up labels from the standards JSON.
 */
export const expandCategories = (categories, language, securityStandard) => {
    if (!categories || !Array.isArray(categories)) return categories;
    const options = Options(language, securityStandard);
    return categories.map(cat => {
        const id = getCategoryValue(cat);
        const option = options.find(o => o.value === id);
        return option ? { value: option.value, label: option.label } : { value: id, label: id };
    });
};

export const categoryIDtoLabel = (id, language, securityStandard) => {
    const options = Options(language, securityStandard);
    const option = options.find(option => option.value === id);
    return option ? option.label : ''; 

};
